import React, { FunctionComponent, useState } from 'react';
import { Paper, Box, makeStyles } from '@material-ui/core';
import { AppointmentList, ConfiguredFields, Order, IColumn } from '../../../../store/modules/application/types';
import Loader from '../../../../components/Loader';
import { getFlatColumnFieldsFromList, getFormattedColumns, getFullAddress } from '../../../../utils/utils';
import TableEmptyState from '../../../../components/TableView/TableEmptyState';
import Modal from '../../../../components/Modal';
import DeleteDialog from '../../../../components/DeleteDialog';
import { useHistory } from 'react-router-dom';
import { BASE_URL } from '../../../../routes';
import InfiniteScrollTableView from '../../../../components/InfiniteScrollTableView';

type AppointmentProps = {
    data: AppointmentList;
    loading: boolean;
    address?: any;
    deleteAppointment: Function;
    skip: number;
    setSkip: Function;
    hasMore: boolean;
    setSortOrder: Function;
    setSortColumn: Function;
    sortOrder: Order;
    sortBy: string;
    appointmentCount: number;
    appointmentColumns: ConfiguredFields;
};
const useStyles = makeStyles({
    tablePaginationInfo: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '10px',
    },
    countSpan: {
        color: '#484848',
        fontSize: 14,
        letterSpacing: 0.1,
        display: 'flex',
        alignItems: 'center',
    },
});

const Appointment: FunctionComponent<AppointmentProps> = (props) => {
    const history = useHistory();
    const classes = useStyles();

    const [appointmentId, setAppointmentId] = useState<string>('');

    /* State for modals */
    const {
        data,
        loading,
        deleteAppointment,
        hasMore,
        skip,
        setSkip,
        setSortColumn,
        setSortOrder,
        sortBy,
        sortOrder,
        appointmentCount,
        appointmentColumns,
    } = props;

    const [isDeleteModalOpen, setModal] = useState(false);

    const columns: IColumn[] = appointmentColumns?.fields?.length
        ? getFormattedColumns(appointmentColumns)
        : [
              {
                  id: 'appointmentDateTime|dateTimeRange',
                  label: 'Date',
                  align: 'left',
                  maxWidth: 300,
                  field: 'appointmentDateTime',
                  sortFields: ['appointmentDateTime'],
              },
              {
                  id: 'type|string',
                  label: 'Appointment Type',
                  align: 'left',
                  maxWidth: 200,
                  field: 'type',
                  sortFields: ['type'],
              },
              {
                  id: 'address|string',
                  label: 'Address',
                  align: 'left',
                  maxWidth: 200,
                  field: 'address',
                  sortFields: ['address'],
              },
              {
                  id: 'participants|string',
                  label: 'Assigned To',
                  align: 'left',
                  maxWidth: 200,
                  field: 'participants',
                  sortFields: ['participants'],
              },
          ];

    const appointments: AppointmentList = appointmentColumns?.fields?.length
        ? getFlatColumnFieldsFromList(data, columns)
        : data?.map((element) => {
              const appointmentTime = {
                  start: element.startDateTime,
                  end: element.endDateTime,
              };
              return {
                  ...element,
                  appointmentDateTime: appointmentTime,
                  address: getFullAddress(element?.address),
                  participants: element?.participants?.map((user) => user?.name).join(', '),
                  type: element?.inspectionType?.name
                      ? element?.type?.name + ' - ' + element?.inspectionType?.name || ''
                      : element?.type?.name,
              };
          });

    const routeChange = (path: string) => {
        history.push(path);
    };

    const onClickRow = (id: string, e: any) => {
        e.stopPropagation();
        routeChange(BASE_URL + `/appointment/${id}/detail`);
    };
    const handleDeleteCase = (id: string, e: any) => {
        e.stopPropagation();
        setAppointmentId(id);
        setModal(true);
    };
    const onClose = () => {
        setModal(false);
    };
    const onDelete = () => {
        setModal(false);
        deleteAppointment(appointmentId);
    };
    const loadMore = () => {
        if (hasMore) {
            setSkip(skip + 1);
        }
    };

    if (loading) {
        return <Loader />;
    }

    return (
        <div className="verticalFillContainer">
            <Box className={classes.tablePaginationInfo}>
                <span className={classes.countSpan}>{`${appointmentCount} Results`}</span>
            </Box>
            <Paper className="case-entries-container verticalFillContent fullHeight">
                {appointments.length > 0 ? (
                    <InfiniteScrollTableView
                        columns={columns}
                        data={appointments}
                        count={appointments.length}
                        onClickRow={onClickRow}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        hasMore={hasMore}
                        loadMore={loadMore}
                        deleteFlag={true}
                        handleDeleteCase={handleDeleteCase}
                        setSortColumn={setSortColumn}
                        setSortOrder={setSortOrder}
                    />
                ) : (
                    <TableEmptyState
                        title="No active appointments."
                        message="All your Appointments will be stored here"
                    ></TableEmptyState>
                )}

                {isDeleteModalOpen && (
                    <Modal open={true} maxWidth={'md'} fullWidth={true} minHeight={'10vh'}>
                        {isDeleteModalOpen && (
                            <DeleteDialog
                                onClose={onClose}
                                onDelete={onDelete}
                                contentTitle="Are you sure you'd like to proceed with this action?"
                                content=""
                            />
                        )}
                    </Modal>
                )}
            </Paper>
        </div>
    );
};

export default Appointment;
